/**
 * Backend persistence for GPS route points (Supabase: `gps_tracks` table).
 *
 * Every GPS update is written here so routes survive sessions/devices and can be
 * replayed in History. Data is isolated per agency via `agency_id`.
 *
 * Required table (run once in Supabase SQL editor):
 *
 *   create table if not exists public.gps_tracks (
 *     id uuid primary key default gen_random_uuid(),
 *     agency_id   text,
 *     vehicle_id  text not null,
 *     lat         double precision not null,
 *     lng         double precision not null,
 *     speed       double precision default 0,
 *     recorded_at timestamptz not null default now(),
 *     created_at  timestamptz not null default now()
 *   );
 *   create index if not exists gps_tracks_lookup
 *     on public.gps_tracks (agency_id, vehicle_id, recorded_at desc);
 *   alter table public.gps_tracks enable row level security;
 *   create policy "gps_tracks_all" on public.gps_tracks
 *     for all using (true) with check (true);
 *
 * (Adjust the RLS policy to match how your other tables are secured.)
 */

import { supabase } from '../config/supabase';

const TABLE = 'gps_tracks';

/** Persist a single GPS point. Fire-and-forget; never throws. */
export const saveTrackPoint = async ({ agencyId, vehicleId, lat, lng, speed = 0, t }) => {
  if (!vehicleId || lat == null || lng == null) return;
  try {
    await supabase.from(TABLE).insert({
      agency_id: agencyId || null,
      vehicle_id: vehicleId,
      lat: +(+lat).toFixed(6),
      lng: +(+lng).toFixed(6),
      speed: Math.round(speed) || 0,
      recorded_at: t ? new Date(t).toISOString() : new Date().toISOString()
    });
  } catch (_) {
    // swallow — live tracking must not break if logging fails
  }
};

/**
 * Fetch a vehicle's stored route points (oldest → newest).
 * @returns {Promise<Array<{lat,lng,s,t}>>}
 */
export const fetchTrack = async ({ agencyId, vehicleId, sinceMs, untilMs }) => {
  if (!vehicleId) return [];
  try {
    let q = supabase
      .from(TABLE)
      .select('lat,lng,speed,recorded_at')
      .eq('vehicle_id', vehicleId)
      .order('recorded_at', { ascending: true })
      .limit(5000);
    if (agencyId) q = q.eq('agency_id', agencyId);
    if (sinceMs) q = q.gte('recorded_at', new Date(sinceMs).toISOString());
    if (untilMs) q = q.lte('recorded_at', new Date(untilMs).toISOString());
    const { data, error } = await q;
    if (error) throw error;
    return (data || []).map(r => ({
      lat: r.lat,
      lng: r.lng,
      s: r.speed || 0,
      t: new Date(r.recorded_at).getTime()
    }));
  } catch (_) {
    return [];
  }
};
